import type { EdgeRef, PendingConnection } from '../editor/connectionMutations.js';
import type { ConnectionStateController } from './connectionState.js';
import type { EdgeVisualStateController } from './edgeVisualState.js';

interface Point {
  x: number;
  y: number;
}

interface EdgeDetachController {
  clearDetachPreview: () => void;
  previewSocketDetach: (io: 'in' | 'out', nodeId: string, portIdx: number, ev?: MouseEvent) => void;
  pickSocketDetachEdge: (io: 'in' | 'out', nodeId: string, portIdx: number, ev?: MouseEvent) => EdgeRef | null;
}

interface PortInteractionDeps {
  connectionState: () => ConnectionStateController;
  edgeDetach: EdgeDetachController;
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point;
  visualState: EdgeVisualStateController;
  reconnectFromInput: (
    nodeId: string,
    portIdx: number,
    getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
  ) => PendingConnection | null;
  renderConnections: () => void;
  renderGraph: () => void;
  renderPendingConnection: () => void;
  forceRender: () => void;
  applyConnectionState: () => void;
  applyValidationState: () => void;
  restoreSnapHighlights: () => void;
}

export function createPortDragHandler(deps: PortInteractionDeps): (dot: HTMLElement) => void {
  let lastPortMouseDown = 0;

  return function setupPortDrag(dot: HTMLElement): void {
    const readDot = (): { io: 'in' | 'out'; nodeId: string; portIdx: number } => ({
      io: dot.dataset.io as 'in' | 'out',
      nodeId: dot.dataset.node!,
      portIdx: parseInt(dot.dataset.port!)
    });

    dot.addEventListener('mouseenter', e => {
      const pendingConn = deps.connectionState().pendingConn;
      if (pendingConn) return;
      if ((e as MouseEvent).ctrlKey || deps.visualState.isCuttingEdges()) {
        deps.edgeDetach.clearDetachPreview();
        return;
      }
      const { io, nodeId, portIdx } = readDot();
      if (io === 'in') deps.edgeDetach.previewSocketDetach(io, nodeId, portIdx, e as MouseEvent);
      else deps.edgeDetach.clearDetachPreview();
    });

    dot.addEventListener('mousemove', e => {
      const pendingConn = deps.connectionState().pendingConn;
      if (pendingConn) return;
      if ((e as MouseEvent).ctrlKey || deps.visualState.isCuttingEdges()) {
        deps.edgeDetach.clearDetachPreview();
        return;
      }
      const { io, nodeId, portIdx } = readDot();
      if (io === 'in') deps.edgeDetach.previewSocketDetach(io, nodeId, portIdx, e as MouseEvent);
      else deps.edgeDetach.clearDetachPreview();
    });

    dot.addEventListener('mouseleave', () => {
      deps.edgeDetach.clearDetachPreview();
    });

    dot.addEventListener('click', e => {
      const pc = deps.connectionState().pendingConn;
      if (!pc) return;
      e.stopPropagation();
      e.preventDefault();
      if (pc.ignoreNextClick) {
        pc.ignoreNextClick = false;
        return;
      }
      const { io, nodeId, portIdx } = readDot();
      const result = deps.connectionState().completePendingConnection(nodeId, portIdx, io);
      if (result.connected || result.typeMismatch) {
        deps.renderGraph();
      }
    });

    dot.addEventListener('mousedown', e => {
      if (e.button !== 0) return;
      const now = Date.now();
      if (now - lastPortMouseDown < 300) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      lastPortMouseDown = now;
      if (deps.connectionState().pendingConn) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      e.preventDefault();

      const { io, nodeId, portIdx } = readDot();

      if (io === 'in') {
        const reconnect = deps.reconnectFromInput(nodeId, portIdx, deps.getDotWorldPos);
        if (reconnect) {
          deps.connectionState().setPendingConn({
            ...reconnect,
            suppressMouseUp: true,
            ignoreNextClick: true,
            startScreen: { x: e.clientX, y: e.clientY }
          });
          deps.renderConnections();
          deps.renderPendingConnection();
          deps.restoreSnapHighlights();
          deps.applyConnectionState();
          deps.applyValidationState();
          deps.forceRender();
          return;
        }

        const pos = deps.getDotWorldPos(nodeId, portIdx, false);
        deps.connectionState().setPendingConn({
          fromNode: null,
          fromPort: null,
          toNode: nodeId,
          toPort: portIdx,
          from: pos,
          current: { ...pos },
          isFromOut: false,
          suppressMouseUp: true,
          ignoreNextClick: true,
          startScreen: { x: e.clientX, y: e.clientY }
        });
      } else {
        const pos = deps.getDotWorldPos(nodeId, portIdx, true);
        deps.connectionState().setPendingConn({
          fromNode: nodeId,
          fromPort: portIdx,
          toNode: null,
          toPort: null,
          from: pos,
          current: { ...pos },
          isFromOut: true,
          suppressMouseUp: true,
          ignoreNextClick: true,
          startScreen: { x: e.clientX, y: e.clientY }
        });
      }

      deps.applyConnectionState();
      deps.renderPendingConnection();
    });
  };
}
